import ConfirmDialog from "component/ConfirmDialog/ConfirmDialog";
import { useEffect, useState, useCallback, memo } from "react";
import { NavigateOptions, useLocation, useNavigate } from "react-router-dom";
import { linkAuthRoute, linkPageLogin } from "routes";
import ACLService from "service/ACLService";
import AuthService from "service/Auth.service";
import { logWithColor } from "util/CommonUtil";

export interface RouteProps {
  navigate?: any;
  navigateNoAuth?: any;
  location?: any;
  goBack?: any;
}
interface NavigateEvent extends Event {
  destination: {
    url: string;
  };
}

export const withRouter = (Component: any, isIgnoreEventRegister?: boolean) => {
  const ViewWrapper = (props: any) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showConfirmDialogue, setShowConfirmDialogue] = useState<boolean>(false);
    const [pendingNavigation, setPendingNavigation] = useState<{
      path: string;
      options?: NavigateOptions;
    } | null>(null);

    const onNavigateWithAuth = useCallback(
      (path: string, options?: NavigateOptions) => {
        const fullPath = `${linkAuthRoute}/${path}`;
        if (ACLService.isFormDirty()) {
          setShowConfirmDialogue(true);
          setPendingNavigation({ path: fullPath, options });
        } else {
          navigate(fullPath, options);
        }
      },
      [navigate]
    );

    const onNavigateNoAuth = useCallback(
      (path: string, options?: NavigateOptions) => {
        if (ACLService.isFormDirty()) {
          setShowConfirmDialogue(true);
          setPendingNavigation({ path, options });
        } else {
          navigate(path, options);
        }
      },
      [navigate]
    );

    const onGoBack = useCallback(() => {
      if (ACLService.isFormDirty()) {
        setShowConfirmDialogue(true);
        setPendingNavigation({ path: "BACK" });
      } else {
        navigate(-1);
      }
    }, [navigate]);

    const onNavigate = useCallback(
      (event: NavigateEvent) => {
        if (!showConfirmDialogue && event.destination && ACLService.isFormDirty()) {
          const destinationUrl = new URL(event.destination.url);
          if (destinationUrl.pathname !== location.pathname) {
            event.preventDefault();
            setShowConfirmDialogue(true);
            setPendingNavigation({
              path: destinationUrl.pathname + destinationUrl.search + destinationUrl.hash,
            });
          }
        }
      },
      [showConfirmDialogue, location.pathname]
    );

    useEffect(() => {
      const { name } = Component;
      logWithColor(`Mounted the component ${name}`, "green");

      if (!isIgnoreEventRegister) {
        window.navigation?.addEventListener("navigate", onNavigate);
        logWithColor(`Event Registered for ${name}`, "yellow");
      }

      return () => {
        if (!isIgnoreEventRegister) {
          window.navigation?.removeEventListener("navigate", onNavigate);
          logWithColor("Event Removed for " + name, "red");
        }
      };
    }, [onNavigate]);

    const handlePrimaryButtonClick = useCallback(() => {
      setShowConfirmDialogue(false);

      if (pendingNavigation) {
        const shouldLogout = pendingNavigation?.options?.state?.shouldLogout;

        ACLService.clearFormDirty();
        if (pendingNavigation.path === "BACK") {
          navigate(-1);
        } else if (pendingNavigation.path === linkPageLogin && shouldLogout) {
          AuthService.logOut();
          navigate(linkPageLogin);
        } else {
          navigate(pendingNavigation.path, pendingNavigation.options);
        }
      }

      setPendingNavigation(null);
    }, [navigate, pendingNavigation]);

    const handleSecondaryButtonClick = useCallback(() => {
      setPendingNavigation(null);
      setShowConfirmDialogue(false);
    }, []);

    return (
      <>
        <ConfirmDialog
          showDialog={showConfirmDialogue}
          closeDialog={handleSecondaryButtonClick}
          headerText={"Leave without saving?"}
          content={"You will lose your progress if you\nchoose to go back now."}
          primaryButtonText={"Yes, leave"}
          secondaryButtonText={"No, Stay"}
          primaryButtonColor=""
          primaryButtonClick={handlePrimaryButtonClick}
        />

        <Component
          navigate={onNavigateWithAuth}
          navigateNoAuth={onNavigateNoAuth}
          goBack={onGoBack}
          location={location}
          {...props}
        />
      </>
    );
  };

  return memo(ViewWrapper);
};
